import { useState, useEffect } from "react";
import { Plus, Edit2, Trash2, Eye, EyeOff, Save, X, Clock, ExternalLink, BookOpen, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import API from "@/lib/api";

const CATEGORIES = ["SMS Marketing", "E-Commerce", "SMS Delivery", "Getting Started", "Security", "Developer", "Finance", "General"];

function BlogForm({ blog, onSave, onCancel }) {
  const [form, setForm] = useState({
    title: blog?.title || "",
    excerpt: blog?.excerpt || "",
    content: blog?.content || "",
    image_url: blog?.image_url || "",
    author: blog?.author || "Sendora Team",
    category: blog?.category || "SMS Marketing",
    tags: blog?.tags?.join(", ") || "",
    read_time: blog?.read_time || 5,
    published: blog?.published !== false,
  });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title.trim() || !form.content.trim()) {
      toast.error("Title and content are required");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        tags: form.tags.split(",").map(t => t.trim()).filter(Boolean),
        read_time: parseInt(form.read_time) || 5,
      };
      if (blog?._id) {
        await API.put(`/blogs/${blog._id}`, payload);
        toast.success("Blog post updated");
      } else {
        await API.post("/blogs", payload);
        toast.success("Blog post created");
      }
      onSave();
    } catch { toast.error("Failed to save blog post"); }
    finally { setSaving(false); }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="sm:col-span-2">
          <Label className="text-xs font-semibold text-slate-700 mb-1.5 block">Title *</Label>
          <Input value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} placeholder="Blog post title" data-testid="blog-form-title" className="border-slate-200" required />
        </div>
        <div className="sm:col-span-2">
          <Label className="text-xs font-semibold text-slate-700 mb-1.5 block">Excerpt (preview text)</Label>
          <textarea value={form.excerpt} onChange={e => setForm(f => ({ ...f, excerpt: e.target.value }))} placeholder="Short summary shown on blog listing..." rows={2} className="w-full border border-slate-200 rounded-lg px-3 py-2.5 text-sm text-slate-900 resize-none focus:outline-none focus:ring-2 focus:ring-[#0c1e41]/20 focus:border-[#0c1e41]" data-testid="blog-form-excerpt" />
        </div>
        <div>
          <Label className="text-xs font-semibold text-slate-700 mb-1.5 block">Category</Label>
          <select value={form.category} onChange={e => setForm(f => ({ ...f, category: e.target.value }))} className="w-full border border-slate-200 rounded-lg px-3 py-2.5 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-[#0c1e41]/20 bg-white" data-testid="blog-form-category">
            {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
        <div>
          <Label className="text-xs font-semibold text-slate-700 mb-1.5 block">Author</Label>
          <Input value={form.author} onChange={e => setForm(f => ({ ...f, author: e.target.value }))} className="border-slate-200" />
        </div>
        <div>
          <Label className="text-xs font-semibold text-slate-700 mb-1.5 block">Featured Image URL</Label>
          <Input value={form.image_url} onChange={e => setForm(f => ({ ...f, image_url: e.target.value }))} placeholder="https://..." className="border-slate-200" data-testid="blog-form-image" />
        </div>
        <div>
          <Label className="text-xs font-semibold text-slate-700 mb-1.5 block">Read Time (minutes)</Label>
          <Input type="number" min="1" max="60" value={form.read_time} onChange={e => setForm(f => ({ ...f, read_time: e.target.value }))} className="border-slate-200" />
        </div>
        <div className="sm:col-span-2">
          <Label className="text-xs font-semibold text-slate-700 mb-1.5 block">Tags (comma-separated)</Label>
          <Input value={form.tags} onChange={e => setForm(f => ({ ...f, tags: e.target.value }))} placeholder="bulk SMS, Nigeria, marketing" className="border-slate-200" />
        </div>
      </div>

      <div>
        <Label className="text-xs font-semibold text-slate-700 mb-1.5 block">Content (HTML) *</Label>
        <textarea
          value={form.content}
          onChange={e => setForm(f => ({ ...f, content: e.target.value }))}
          placeholder="<h2>Introduction</h2><p>Your content here...</p>"
          rows={12}
          className="w-full border border-slate-200 rounded-lg px-3 py-2.5 text-sm text-slate-900 font-mono resize-y focus:outline-none focus:ring-2 focus:ring-[#0c1e41]/20 focus:border-[#0c1e41]"
          data-testid="blog-form-content"
          required
        />
        <p className="text-xs text-slate-400 mt-1">Use HTML tags: &lt;h2&gt;, &lt;p&gt;, &lt;ul&gt;, &lt;li&gt;, &lt;strong&gt;, &lt;pre&gt;&lt;code&gt;&lt;/code&gt;&lt;/pre&gt;</p>
      </div>

      <div className="flex items-center gap-3">
        <label className="flex items-center gap-2 cursor-pointer select-none">
          <div
            onClick={() => setForm(f => ({ ...f, published: !f.published }))}
            className={`w-10 h-5 rounded-full transition-colors relative ${form.published ? "bg-[#0c1e41]" : "bg-slate-300"}`}
          >
            <div className={`absolute top-0.5 w-4 h-4 rounded-full bg-white transition-transform ${form.published ? "translate-x-5" : "translate-x-0.5"}`} />
          </div>
          <span className="text-sm text-slate-700">{form.published ? "Published" : "Draft"}</span>
        </label>
      </div>

      <div className="flex gap-3 pt-2">
        <Button type="submit" disabled={saving} className="bg-[#0c1e41] hover:bg-[#091629] text-white" data-testid="blog-form-submit">
          {saving ? <><RefreshCw size={14} className="mr-2 animate-spin" /> Saving…</> : <><Save size={14} className="mr-2" /> {blog?._id ? "Update Post" : "Publish Post"}</>}
        </Button>
        <Button type="button" onClick={onCancel} variant="outline" className="border-slate-300 text-slate-600">
          <X size={14} className="mr-2" /> Cancel
        </Button>
      </div>
    </form>
  );
}

export default function AdminBlog() {
  const [blogs, setBlogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState("list"); // "list" | "create" | "edit"
  const [editing, setEditing] = useState(null);
  const [deleting, setDeleting] = useState(null);

  useEffect(() => { loadBlogs(); }, []);

  const loadBlogs = async () => {
    setLoading(true);
    try {
      const { data } = await API.get("/blogs/admin/all?limit=50");
      setBlogs(data.blogs || []);
      setTotal(data.total || 0);
    } catch { toast.error("Failed to load blog posts"); }
    finally { setLoading(false); }
  };

  const handleTogglePublish = async (blog) => {
    try {
      await API.put(`/blogs/${blog._id}`, { published: !blog.published });
      setBlogs(prev => prev.map(b => b._id === blog._id ? { ...b, published: !b.published } : b));
      toast.success(blog.published ? "Post unpublished" : "Post published");
    } catch { toast.error("Update failed"); }
  };

  const handleDelete = async (id) => {
    try {
      await API.delete(`/blogs/${id}`);
      setBlogs(prev => prev.filter(b => b._id !== id));
      setTotal(t => t - 1);
      setDeleting(null);
      toast.success("Blog post deleted");
    } catch { toast.error("Delete failed"); }
  };

  const afterSave = () => { setView("list"); setEditing(null); loadBlogs(); };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-extrabold text-slate-900 flex items-center gap-2">
            <BookOpen size={22} /> Blog Management
          </h1>
          <p className="text-sm text-slate-500 mt-1">{total} post{total !== 1 ? "s" : ""} total</p>
        </div>
        {view === "list" && (
          <Button onClick={() => setView("create")} className="bg-[#0c1e41] hover:bg-[#091629] text-white h-9" data-testid="blog-create-button">
            <Plus size={15} className="mr-2" /> New Post
          </Button>
        )}
      </div>

      {(view === "create" || view === "edit") && (
        <div className="bg-white border border-slate-200 rounded-2xl p-6 shadow-sm">
          <h2 className="text-lg font-bold text-slate-900 mb-5">{view === "edit" ? "Edit Post" : "Create New Post"}</h2>
          <BlogForm blog={editing} onSave={afterSave} onCancel={() => { setView("list"); setEditing(null); }} />
        </div>
      )}

      {view === "list" && (
        <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden shadow-sm">
          <div className="px-5 py-4 border-b border-slate-200 flex items-center gap-2">
            <BookOpen size={16} className="text-slate-400" />
            <h3 className="text-sm font-semibold text-slate-900">All Posts</h3>
          </div>
          {loading ? (
            <div className="divide-y divide-slate-100">
              {[...Array(5)].map((_, i) => <div key={i} className="h-16 bg-slate-100 animate-pulse mx-5 my-2 rounded" />)}
            </div>
          ) : blogs.length === 0 ? (
            <div className="py-12 text-center text-slate-400">No blog posts yet. Create your first post!</div>
          ) : (
            <div className="divide-y divide-slate-100">
              {blogs.map(b => (
                <div key={b._id} data-testid="admin-blog-row" className="flex items-center gap-4 px-5 py-4 hover:bg-slate-50">
                  {b.image_url && (
                    <img src={b.image_url} alt={b.title} className="w-14 h-10 rounded-lg object-cover shrink-0 hidden sm:block" />
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-slate-900 truncate">{b.title}</p>
                    <div className="flex items-center gap-2 mt-0.5">
                      <span className="text-[10px] font-medium bg-slate-100 text-slate-500 px-2 py-0.5 rounded-full">{b.category}</span>
                      <span className="text-xs text-slate-400 flex items-center gap-0.5"><Clock size={10} /> {b.read_time}m</span>
                      <span className="text-xs text-slate-400">{b.views || 0} views</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${b.published ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>
                      {b.published ? "Live" : "Draft"}
                    </span>
                    <button
                      onClick={() => handleTogglePublish(b)}
                      title={b.published ? "Unpublish" : "Publish"}
                      className="p-1.5 hover:bg-slate-100 rounded-lg text-slate-400 hover:text-[#0c1e41] transition-colors"
                      data-testid="blog-toggle-publish"
                    >
                      {b.published ? <EyeOff size={14} /> : <Eye size={14} />}
                    </button>
                    <a href={`/blog/${b.slug}`} target="_blank" rel="noopener noreferrer" className="p-1.5 hover:bg-slate-100 rounded-lg text-slate-400 hover:text-[#0c1e41] transition-colors">
                      <ExternalLink size={14} />
                    </a>
                    <button
                      onClick={() => { setEditing(b); setView("edit"); }}
                      className="p-1.5 hover:bg-slate-100 rounded-lg text-slate-400 hover:text-[#0c1e41] transition-colors"
                      data-testid="blog-edit-button"
                    >
                      <Edit2 size={14} />
                    </button>
                    {deleting === b._id ? (
                      <div className="flex gap-1">
                        <button onClick={() => handleDelete(b._id)} className="px-2 py-1 bg-red-500 text-white text-xs rounded-lg hover:bg-red-600" data-testid="blog-confirm-delete">Confirm</button>
                        <button onClick={() => setDeleting(null)} className="px-2 py-1 bg-slate-200 text-slate-600 text-xs rounded-lg">Cancel</button>
                      </div>
                    ) : (
                      <button onClick={() => setDeleting(b._id)} className="p-1.5 hover:bg-red-50 rounded-lg text-slate-400 hover:text-red-600 transition-colors" data-testid="blog-delete-button">
                        <Trash2 size={14} />
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
